import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Card,
  Row,
  Col,
  Tag,
  Select,
  DatePicker,
  Spin,
  Empty,
  Alert,
  Typography,
  Space,
  message,
} from "antd";
import axiosInstance from "api/axiosInstance";
import API from "api/endpoints";
import FlightRiskService from "Services/FlightRiskService";

const { Text } = Typography;

const riskColor = (level) => {
  const l = String(level || "").toLowerCase();
  if (l === "high") return "red";
  if (l === "medium") return "orange";
  if (l === "low") return "green";
  return "default";
};

// Shared Flight Risk / Retention insight view (Jun-21 AI handoff).
// mode="supervisor" -> direct reports; mode="admin" -> company-wide + dept filter.
// Advisory only: insights are a list/card view; no profile navigation (the
// response has no employeeId).
const FlightRiskPanel = ({ mode = "supervisor" }) => {
  const isAdmin = mode === "admin";
  const [asOfDate, setAsOfDate] = useState(null);
  const [departmentId, setDepartmentId] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const res = await axiosInstance.get(API.department.getList);
        setDepartments(Array.isArray(res.data) ? res.data : res.data?.data || []);
      } catch (e) {
        // Department filter is optional; ignore load failure.
      }
    })();
  }, [isAdmin]);

  const load = async () => {
    try {
      setLoading(true);
      const data = isAdmin
        ? await FlightRiskService.getAdmin(asOfDate, departmentId)
        : await FlightRiskService.getSupervisor(asOfDate);
      setResult(data || null);
    } catch (e) {
      console.error("Failed to load flight-risk insights", e);
      message.error("Failed to load flight-risk insights");
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asOfDate, departmentId]);

  const insights = Array.isArray(result?.insights)
    ? result.insights
    : Array.isArray(result?.atRisk)
    ? result.atRisk
    : Array.isArray(result?.at_risk)
    ? result.at_risk
    : [];
  const highRiskCount =
    result?.highRiskCount ??
    result?.high_risk_count ??
    insights.filter((x) => String(x?.riskLevel || x?.risk_level).toLowerCase() === "high").length;
  const aiUnavailable = result && result.isAiInsightAvailable === false;
  const departmentOptions = (Array.isArray(departments) ? departments : []).map((d) => ({
    value: d.id ?? d.departmentId,
    label: d.name ?? d.departmentName,
  }));

  return (
    <>
      <Space wrap style={{ marginBottom: 16 }}>
        {isAdmin && (
          <Select
            placeholder="All Departments"
            allowClear
            style={{ width: 200 }}
            value={departmentId}
            onChange={(v) => setDepartmentId(v ?? null)}
            options={departmentOptions}
          />
        )}
        <DatePicker
          placeholder="As of (today)"
          format="YYYY-MM-DD"
          onChange={(d) => setAsOfDate(d ? d.format("YYYY-MM-DD") : null)}
        />
      </Space>

      {loading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          {aiUnavailable && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={result.message || "Flight-risk insights are temporarily unavailable."}
            />
          )}
          {insights.length > 0 && (
            <Space style={{ marginBottom: 12 }}>
              <Tag color="red">High risk: {highRiskCount}</Tag>
              <Tag>Total flagged: {insights.length}</Tag>
            </Space>
          )}
          {insights.length === 0 ? (
            <Empty
              description={
                aiUnavailable
                  ? "Insights will appear here once the AI is available."
                  : "No at-risk employees right now. Nothing to flag."
              }
            />
          ) : (
            <Row gutter={[16, 16]}>
              {insights.map((it, i) => {
                const name = it.employeeName || it.name || "-";
                const department = it.department || it.departmentName;
                const level = it.riskLevel || it.risk_level;
                const why = it.why || it.reason;
                const clientImpact = it.clientImpact || it.client_impact;
                const suggestedAction = it.suggestedAction || it.suggested_action;
                return (
                  <Col xs={24} md={12} key={`${name}-${i}`}>
                    <Card size="small" className="card-professional">
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: 8,
                        }}
                      >
                        <div>
                          <Text strong>{name}</Text>
                          {department && (
                            <div style={{ fontSize: 12, color: "#888" }}>{department}</div>
                          )}
                        </div>
                        <Tag color={riskColor(level)}>{level || "Unknown"} risk</Tag>
                      </div>
                      {why && (
                        <div style={{ marginBottom: 6 }}>
                          <b>Why:</b> {why}
                        </div>
                      )}
                      {clientImpact && (
                        <div style={{ marginBottom: 6 }}>
                          <b>Client impact:</b> {clientImpact}
                        </div>
                      )}
                      {suggestedAction && (
                        <div
                          style={{
                            marginTop: 8,
                            padding: "8px 10px",
                            background: "#F6FFED",
                            border: "1px solid #B7EB8F",
                            borderRadius: 6,
                          }}
                        >
                          <b>Suggested action:</b> {suggestedAction}
                        </div>
                      )}
                    </Card>
                  </Col>
                );
              })}
            </Row>
          )}
        </>
      )}
    </>
  );
};

FlightRiskPanel.propTypes = {
  mode: PropTypes.oneOf(["supervisor", "admin"]),
};

export default FlightRiskPanel;
